import moment from 'moment';
import { ZanbatoAskPricingItem } from 'api/models/ZanbatoAskPricingItem';
import { ZanbatoBidPricingItem } from 'api/models/ZanbatoBidPricingItem';
import { ZanbatoClosedTransaction } from 'api/models/ZanbatoClosedTransaction';
import { ZanbatoPricingItem } from 'api/models/ZanbatoPricingItem';
import { ChartDataPoint, ChartSeries } from 'components/Charts/types';

// ---------------------------------------------------------------------------
// Series config
// ---------------------------------------------------------------------------

export interface SeriesConfig {
  id: string;
  label: string;
  color: string;
  defaultOn: boolean;
}

export const SERIES_CONFIGS: SeriesConfig[] = [
  { id: 'zx_current', label: 'ZX Index Value (Current)', color: '#1976d2', defaultOn: true },
  { id: 'zx_trailing', label: 'ZX Index Value (Trailing)', color: '#ed9c28', defaultOn: true },
  { id: 'closed_transactions', label: 'Closed Transactions', color: '#00796b', defaultOn: true },
  { id: 'ask', label: 'Ask Pricing', color: '#2e7d32', defaultOn: false },
  { id: 'bid', label: 'Bid Pricing', color: '#c62828', defaultOn: false },
];

export const DEFAULT_ENABLED_IDS = new Set(SERIES_CONFIGS.filter(s => s.defaultOn).map(s => s.id));

// ---------------------------------------------------------------------------
// Period
// ---------------------------------------------------------------------------

export const Period = { M3: '3M', M6: '6M', Y1: '1Y', Y2: '2Y', YTD: 'YTD', All: 'Latest' } as const;
export type PeriodValue = (typeof Period)[keyof typeof Period];
export const PERIODS = Object.values(Period) as PeriodValue[];

export const getPeriodStartDate = (endDate: string, period: PeriodValue): string | null => {
  switch (period) {
    case Period.M3:
      return moment(endDate).subtract(3, 'months').format('YYYY-MM-DD');
    case Period.M6:
      return moment(endDate).subtract(6, 'months').format('YYYY-MM-DD');
    case Period.Y1:
      return moment(endDate).subtract(12, 'months').format('YYYY-MM-DD');
    case Period.Y2:
      return moment(endDate).subtract(24, 'months').format('YYYY-MM-DD');
    case Period.YTD:
      return `${endDate.slice(0, 4)}-01-01`;
    case Period.All:
      return null;
    default:
      return null;
  }
};

export const filterByPeriod = <T extends { report_period?: string | null }>(
  items: T[],
  period: PeriodValue,
  endDate: string
): T[] => {
  if (period === Period.All) {
    return items.filter(i => i.report_period);
  }
  const startDate = getPeriodStartDate(endDate, period);
  return items.filter(i => {
    if (!i.report_period) {
      return false;
    }
    if (i.report_period > endDate) {
      return false;
    }
    if (startDate && i.report_period < startDate) {
      return false;
    }
    return true;
  });
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

export const toNum = (val: string | number | null | undefined): number | null => {
  if (val === null || val === undefined) {
    return null;
  }
  const n = typeof val === 'string' ? parseFloat(val) : val;
  return isNaN(n) ? null : n;
};

export const formatCurrency = (val: number | null): string => {
  if (val === null) {
    return '—';
  }
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatCurrencyStr = (val: string | null | undefined): string => formatCurrency(toNum(val));

export const formatVolume = (val: string | null | undefined): string => {
  const n = toNum(val);
  if (n === null) {
    return '—';
  }
  if (n >= 1_000) {
    return `$${(n / 1_000).toFixed(1)}B`;
  }
  if (n >= 1) {
    return `$${n.toFixed(1)}M`;
  }
  return `$${(n * 1_000).toFixed(0)}K`;
};

/** "2024-07-31" → "Jul 31 '24" */
export const formatPricingDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month) {
    return dateStr;
  }
  const monthLabel = new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short' });
  const dayPart = day ? ` ${day}` : '';
  return `${monthLabel}${dayPart} '${String(year).slice(2)}`;
};

// ---------------------------------------------------------------------------
// As-of record
// ---------------------------------------------------------------------------

export const getAsOfRecord = (
  items: ZanbatoPricingItem[],
  measurementDate: string | undefined
): ZanbatoPricingItem | null => {
  const sorted = [...items]
    .filter(i => i.report_period)
    .sort((a, b) => b.report_period!.localeCompare(a.report_period!));
  if (!measurementDate) {
    return sorted[0] ?? null;
  }
  return sorted.find(i => i.report_period! <= measurementDate) ?? sorted[0] ?? null;
};

// ---------------------------------------------------------------------------
// Header stats
// ---------------------------------------------------------------------------

export interface HeaderStats {
  currentValue: number | null;
  change: { abs: number; pct: number } | null;
}

export const computeHeaderStats = (items: ZanbatoPricingItem[], measurementDate: string | undefined): HeaderStats => {
  const asOf = getAsOfRecord(items, measurementDate);
  const currentValue = toNum(asOf?.zx_index_value);

  const sorted = [...items]
    .filter(i => i.report_period && toNum(i.zx_index_value) !== null)
    .sort((a, b) => a.report_period!.localeCompare(b.report_period!));

  const first = toNum(sorted[0]?.zx_index_value);
  const last = toNum(sorted[sorted.length - 1]?.zx_index_value);
  const change =
    first !== null && last !== null && first !== 0 ? { abs: last - first, pct: ((last - first) / first) * 100 } : null;

  return { currentValue, change };
};

// ---------------------------------------------------------------------------
// Robustness badge
// ---------------------------------------------------------------------------

export interface ScoreRange {
  label: string;
  confidence: string;
  color: 'error' | 'warning' | 'success';
  description: string;
}

export const ROBUSTNESS_SCORE_RANGES: ScoreRange[] = [
  {
    label: '1\u20132',
    confidence: 'Low Confidence',
    color: 'error',
    description: 'Companies with fewer than 3 orders on either side of the market.',
  },
  {
    label: '3\u20134',
    confidence: 'Low Confidence',
    color: 'error',
    description:
      'Companies with 3\u20135 orders on each side of the market or a more asymmetric orderbook with 5+ orders. ' +
      'Most companies with scores of 3 or 4 do not have recently executed trades in the data set.',
  },
  {
    label: '5',
    confidence: 'Med Confidence',
    color: 'warning',
    description:
      'Typically indicates at least one executed trade or a more balanced orderbook, but with no recently executed trades.',
  },
  {
    label: '6',
    confidence: 'Med Confidence',
    color: 'warning',
    description:
      'Usually includes at least one executed trade in the data set, with some edge cases reflecting ' +
      'a deep orderbook of 10+ orders on both sides of the market.',
  },
  {
    label: '7\u201310',
    confidence: 'High Confidence',
    color: 'success',
    description:
      'Reflects deeper order books as well as multiple executed trades, indicating a higher level of confidence in the price data.',
  },
];

export const ROBUSTNESS_METHODOLOGY =
  'The ZXData Robustness Score is a proprietary algorithm developed by Zanbato to ' +
  'objectively assess the confidence levels in the ZX Index Value for different private ' +
  'companies. This score is calculated on a scale of 1 to 10 based on several key metrics: ' +
  'number of executed trades and orders, age of closed transactions and orders, total volume ' +
  'of trading activity, spread between bid and offer price, and symmetry of the orderbook.';

const getScoreDescription = (score: number): string => {
  if (score <= 2) {
    return 'Companies with fewer than 3 orders on either side of the market.';
  }
  if (score <= 4) {
    return (
      'Companies with 3\u20135 orders on each side of the market or a more asymmetric orderbook ' +
      'with 5+ orders. Most companies with scores of 3 or 4 do not have recently executed trades ' +
      'in the data set.'
    );
  }
  if (score === 5) {
    return (
      'Typically indicates at least one executed trade or a more balanced orderbook, ' +
      'but with no recently executed trades.'
    );
  }
  if (score === 6) {
    return (
      'Usually includes at least one executed trade in the data set, with some edge cases ' +
      'reflecting a deep orderbook of 10+ orders on both sides of the market.'
    );
  }
  return (
    'Reflects deeper order books as well as multiple executed trades, indicating a higher ' +
    'level of confidence in the price data.'
  );
};

export interface RobustnessInfo {
  label: string;
  confidence: string;
  color: 'error' | 'warning' | 'success';
  scoreDescription: string;
}

export const getRobustnessInfo = (score: number | null | undefined): RobustnessInfo | null => {
  if (score === null || score === undefined) {
    return null;
  }
  if (score <= 3) {
    return {
      label: `RS${score}`,
      confidence: 'Low Confidence',
      color: 'error',
      scoreDescription: getScoreDescription(score),
    };
  }
  if (score <= 6) {
    return {
      label: `RS${score}`,
      confidence: 'Med Confidence',
      color: 'warning',
      scoreDescription: getScoreDescription(score),
    };
  }
  return {
    label: `RS${score}`,
    confidence: 'High Confidence',
    color: 'success',
    scoreDescription: getScoreDescription(score),
  };
};

// ---------------------------------------------------------------------------
// Series builders
// ---------------------------------------------------------------------------

const toDataPoints = (
  items: { report_period?: string | null }[],
  getValue: (item: any) => number | null
): ChartDataPoint[] =>
  items
    .filter(i => i.report_period && getValue(i) !== null)
    .sort((a, b) => a.report_period!.localeCompare(b.report_period!))
    .map(i => ({ x: i.report_period!, y: getValue(i)! }));

export const buildAllSeries = (
  enabledIds: Set<string>,
  pricing: ZanbatoPricingItem[],
  ask: ZanbatoAskPricingItem[],
  bid: ZanbatoBidPricingItem[],
  transactions: ZanbatoClosedTransaction[]
): ChartSeries[] => {
  const series: ChartSeries[] = [];

  if (enabledIds.has('zx_current')) {
    series.push({
      id: 'zx_current',
      label: 'ZX Index Value (Current)',
      color: '#1976d2',
      data: toDataPoints(pricing, i => toNum(i.zx_index_value)),
      config: { connectNulls: true },
    });
  }

  if (enabledIds.has('zx_trailing')) {
    series.push({
      id: 'zx_trailing',
      label: 'ZX Index Value (Trailing)',
      color: '#ed9c28',
      data: toDataPoints(pricing, i => toNum(i.zx_index_value_trailing)),
      config: { lineStyle: { type: 'dashed' }, connectNulls: true },
    });
  }

  if (enabledIds.has('ask')) {
    series.push({
      id: 'ask',
      label: 'Ask Pricing',
      color: '#2e7d32',
      data: toDataPoints(ask, i => toNum(i.ask_zx_index_value)),
      config: { connectNulls: true },
    });
  }

  if (enabledIds.has('bid')) {
    series.push({
      id: 'bid',
      label: 'Bid Pricing',
      color: '#c62828',
      data: toDataPoints(bid, i => toNum(i.bid_zx_index_value)),
      config: { connectNulls: true },
    });
  }

  if (enabledIds.has('closed_transactions')) {
    // Group by closing_date and average price_per_share for same-day transactions
    const byDate = new Map<string, number[]>();
    transactions.forEach(t => {
      if (!t.closing_date) {
        return;
      }
      const price = toNum(t.price_per_share);
      if (price === null) {
        return;
      }
      const bucket = byDate.get(t.closing_date) ?? [];
      bucket.push(price);
      byDate.set(t.closing_date, bucket);
    });
    const data: ChartDataPoint[] = Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, prices]) => ({
        x: date,
        y: prices.reduce((sum, p) => sum + p, 0) / prices.length,
      }));
    series.push({
      id: 'closed_transactions',
      label: 'Closed Transactions',
      color: '#00796b',
      data,
      config: { type: 'scatter', symbol: 'circle', symbolSize: 7 },
    });
  }

  return series;
};

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

type ExtraField = { label: string; value: string };
type TooltipExtras = Map<string, Map<string, ExtraField[]>>;

export const buildTooltipExtras = (
  pricing: ZanbatoPricingItem[],
  ask: ZanbatoAskPricingItem[],
  bid: ZanbatoBidPricingItem[],
  transactions: ZanbatoClosedTransaction[]
): TooltipExtras => {
  const extras: TooltipExtras = new Map();

  const pricingMap = new Map<string, ExtraField[]>();
  pricing.forEach(i => {
    if (!i.report_period) {
      return;
    }
    pricingMap.set(i.report_period, [
      { label: 'Volume', value: formatVolume(i.total_volume) },
      { label: 'DWA', value: formatCurrencyStr(i.overall_dollar_weighted_average) },
      { label: 'Simple Avg', value: formatCurrencyStr(i.overall_simple_average) },
      { label: 'Robustness', value: i.robustness != null ? `RS${i.robustness}` : '—' },
    ]);
  });
  extras.set('ZX Index Value (Current)', pricingMap);

  const trailingMap = new Map<string, ExtraField[]>();
  pricing.forEach(i => {
    if (!i.report_period) {
      return;
    }
    trailingMap.set(i.report_period, [{ label: 'Current', value: formatCurrencyStr(i.zx_index_value) }]);
  });
  extras.set('ZX Index Value (Trailing)', trailingMap);

  const askMap = new Map<string, ExtraField[]>();
  ask.forEach(i => {
    if (!i.report_period) {
      return;
    }
    askMap.set(i.report_period, [
      { label: 'Volume', value: formatVolume(i.ask_volume) },
      { label: 'DWA', value: formatCurrencyStr(i.ask_dollar_weighted_average) },
      { label: 'Simple Avg', value: formatCurrencyStr(i.ask_simple_average) },
      { label: 'Robustness', value: i.robustness != null ? `RS${i.robustness}` : '—' },
    ]);
  });
  extras.set('Ask Pricing', askMap);

  const bidMap = new Map<string, ExtraField[]>();
  bid.forEach(i => {
    if (!i.report_period) {
      return;
    }
    bidMap.set(i.report_period, [
      { label: 'Volume', value: formatVolume(i.bid_volume) },
      { label: 'DWA', value: formatCurrencyStr(i.bid_dollar_weighted_average) },
      { label: 'Simple Avg', value: formatCurrencyStr(i.bid_simple_average) },
      { label: 'Robustness', value: i.robustness != null ? `RS${i.robustness}` : '—' },
    ]);
  });
  extras.set('Bid Pricing', bidMap);

  const txByDate = new Map<string, ZanbatoClosedTransaction[]>();
  transactions.forEach(t => {
    if (!t.closing_date) {
      return;
    }
    const bucket = txByDate.get(t.closing_date) ?? [];
    bucket.push(t);
    txByDate.set(t.closing_date, bucket);
  });
  const closedTransactionsMap = new Map<string, ExtraField[]>();
  txByDate.forEach((txs, date) => {
    const fields: ExtraField[] = [];
    txs.forEach((t, i) => {
      if (i > 0) {
        fields.push({ label: '─────', value: '' });
      }
      if (t.security_type) {
        fields.push({ label: 'Security', value: t.security_type });
      }
      if (t.structure) {
        fields.push({ label: 'Structure', value: t.structure });
      }
      if (t.total_value) {
        fields.push({ label: 'Total Value', value: t.total_value });
      }
      if (t.rofr_percentage != null) {
        const pct = parseFloat(t.rofr_percentage);
        if (!Number.isNaN(pct)) {
          fields.push({ label: 'ROFR', value: `${(pct * 100).toFixed(0)}%` });
        }
      }
    });
    closedTransactionsMap.set(date, fields);
  });
  extras.set('Closed Transactions', closedTransactionsMap);

  return extras;
};

interface EChartsTooltipParam {
  axisValue: string;
  value: number | null;
  seriesName: string;
  marker: string;
}

export const buildTooltipFormatter =
  (extras: TooltipExtras) =>
    (params: EChartsTooltipParam[]): string => {
      if (!Array.isArray(params) || !params.length) {
        return '';
      }
      const period = String(params[0].axisValue);
      let html = '<div style="min-width:210px;padding:2px">';
      html += `<div style="font-weight:700;margin-bottom:8px;font-size:0.82rem">${formatPricingDate(period)}</div>`;

      params.forEach(p => {
        const val = toNum(p.value);
        if (val !== null) {
          html += '<div style="margin-bottom:6px">';
          html += '<div style="display:flex;align-items:center;gap:4px;margin-bottom:2px">';
          html += `${p.marker}<span style="font-size:0.82rem">${p.seriesName}</span>`;
          html += `<span style="font-weight:700;margin-left:auto;padding-left:12px">${formatCurrency(val)}</span>`;
          html += '</div>';
          const seriesExtras = extras.get(p.seriesName)?.get(period);
          if (seriesExtras?.length) {
            seriesExtras.forEach(({ label, value }) => {
              html += `<div style="padding-left:18px;color:#888;font-size:0.78rem">${label}: <span style="color:#555;font-weight:600">${value}</span></div>`;
            });
          }
          html += '</div>';
        }
      });
      html += '</div>';
      return html;
    };
